import Button from '@ingka/button';
import archiveBoxPath from '@ingka/ssr-icon/paths/archive-box';
import locationPinPath from '@ingka/ssr-icon/paths/location-pin';
import personPath from '@ingka/ssr-icon/paths/person';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { signIn, signOut } from '@/api/client';
import ikeaLogo from '@/assets/ikea.svg';
import { useApp } from '@/context/AppContext';
import styles from './AppHeader.module.css';

interface Props {
  showArchive?: boolean;
  showAuth?: boolean;
  showLocations?: boolean;
}

export default function AppHeader({
  showArchive = true,
  showAuth = true,
  showLocations: showLocationsProp = true,
}: Props) {
  const navigate = useNavigate();
  const { user, handleSignout } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);

  const showLocations =
    showLocationsProp &&
    (user?.role === 'admin' ||
      user?.product_scope?.includes('*') ||
      user?.product_scope?.includes('locations'));

  const onLogout = async () => {
    setMenuOpen(false);
    try {
      await signOut();
    } catch {
      /* ignore */
    }
    handleSignout();
  };

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        {/* Brand */}
        <button
          type="button"
          className={styles.brand}
          onClick={() => navigate(user ? '/upload/product' : '/')}
          aria-label="CMPMaster — go to home"
        >
          <img src={ikeaLogo} alt="IKEA" className={styles.brandLogo} />
          <span className={styles.brandName}>CMPMaster</span>
        </button>

        {/* Nav */}
        <nav className={styles.nav}>
          {showLocations && (
            <Button
              type="tertiary"
              size="small"
              text="Locations"
              ssrIcon={locationPinPath}
              onClick={() => navigate('/locations')}
            />
          )}

          {user && (
            <Button
              type="tertiary"
              size="small"
              text="Data catalog"
              onClick={() => navigate('/data-catalog')}
            />
          )}

          {showArchive && (
            <Button
              type="tertiary"
              size="small"
              text="Archive"
              ssrIcon={archiveBoxPath}
              onClick={() => navigate('/archive')}
            />
          )}

          {user?.role === 'admin' && (
            <Button
              type="tertiary"
              size="small"
              text="Admin"
              ssrIcon={personPath}
              onClick={() => navigate('/admin/users')}
            />
          )}

          {showAuth && (
            user ? (
                <div className={styles.avatarWrapper}>
                  <button
                    type="button"
                    className={styles.avatar}
                    onClick={() => setMenuOpen((o) => !o)}
                    aria-label={`Signed in as ${user.name}. Open account menu.`}
                    aria-haspopup="true"
                    aria-expanded={menuOpen}
                  >
                    {user.email[0]?.toUpperCase()}
                  </button>

                  {menuOpen && (
                    <>
                      <button
                        type="button"
                        className={styles.menuBackdrop}
                        aria-label="Close account menu"
                        onClick={() => setMenuOpen(false)}
                      />
                      <div className={styles.menu}>
                        <div className={styles.menuUser}>
                          <div className={styles.menuUserName}>{user.name}</div>
                          <div className={styles.menuUserEmail}>{user.email}</div>
                        </div>
                        <button type="button" className={styles.menuSignOut} onClick={onLogout}>
                          Sign out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Button type="emphasised" size="small" text="Sign in" onClick={() => signIn()} />
              )
          )}
        </nav>
      </div>
    </header>
  );
}
